import { ChangeDetectionStrategy, Component, ElementRef, HostListener, inject, OnInit, ViewChild } from '@angular/core';
import { CampaignInfo } from '@core/models/swagger/initFlow';
import { ValidationService } from '@core/services/api/http/validation.service';
import { ModalController } from '@ionic/angular';
import { select, Store } from '@ngrx/store';
import { AppState } from '@store/app.state';
import { getInitFlow } from '@store/flow-data/init-flow';
import dayjs from 'dayjs';
import isBetween from 'dayjs/plugin/isBetween';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import { openUrl } from '@global/utils';
import { LinkOpenEnum } from '@global/enums';
import { DomSanitizer } from '@angular/platform-browser';

dayjs.extend(isBetween);
dayjs.extend(customParseFormat);

@Component({
  selector: 'qb-de-exit-intent-modal',
  templateUrl: './exit-intent-modal.component.html',
  styleUrls: ['./exit-intent-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class QbExitIntentModalComponent implements OnInit {
  protected readonly validationService: ValidationService = inject(ValidationService);

  @ViewChild('callButton') callButtonRef!: ElementRef<HTMLButtonElement>;
  @ViewChild('closeButton') closeButtonRef!: ElementRef<HTMLButtonElement>;
  @ViewChild('modalRoot') modalRootRef!: ElementRef<HTMLDivElement>;

  protected destroy$: Subject<boolean> = new Subject<boolean>();
  serviceLoading?: Observable<boolean>;

  campaignInfo: CampaignInfo;
  isCallAvailable: boolean;

  title: string;
  description: string;
  btnLabel: string;

  constructor(
    private readonly modalCtrl: ModalController,
    private readonly store: Store<AppState>,
    private readonly translateService: TranslateService,
    private readonly sanitizer: DomSanitizer
  ) {}

  ngOnInit(): void {
    this.store.pipe(select(getInitFlow), takeUntil(this.destroy$)).subscribe(initFlowData => {
      this.campaignInfo = initFlowData?.campaignInfo;
      this.setCallAvailability();
      this.setLabels();
    });
  }

  close(): void {
    this.modalCtrl.dismiss();
  }

  private setCallAvailability(): void {
    const availablePhoneFrom = this.campaignInfo.availablePhoneFrom;
    const availablePhoneTo = this.campaignInfo.availablePhoneTo;

    if (!availablePhoneFrom || !availablePhoneTo) {
      this.isCallAvailable = false;
      return;
    }

    const now = dayjs();
    const dayOfWeek = now.day();
    
    const isWeekend = (dayOfWeek === 0 || dayOfWeek === 6);
    if (isWeekend) {
      this.isCallAvailable = false;
      return;
    }

    const from = dayjs(availablePhoneFrom, 'HH:mm');
    const to = dayjs(availablePhoneTo, 'HH:mm');

    this.isCallAvailable = now.isBetween(from, to, null, '[]');
  }

  private setLabels(): void {
    this.title = this.campaignInfo.exitIntentLayerTitle ?? this.translateService.instant('EXIT-INTENT-MODAL.TITLE');

     this.description = this.isCallAvailable
      ? this.campaignInfo.exitIntentLayerDescription
      : this.campaignInfo.unavailablePhoneDescription ?? this.translateService.instant('EXIT-INTENT-MODAL.SUBTITLE');

    this.btnLabel = this.isCallAvailable
      ? this.campaignInfo.exitIntentLayerButtonText
      : this.campaignInfo.unavailablePhoneButtonUrlTitle ?? this.translateService.instant('EXIT-INTENT-MODAL.BUTTON');
  }

  onBtnClick(): void {
    const url = this.isCallAvailable
      ? `tel: ${this.campaignInfo.telephoneNumber}`
      : this.campaignInfo.unavailablePhoneButtonUrl ?? `tel: ${this.campaignInfo.telephoneNumber}`;

    openUrl(url, LinkOpenEnum.NEW_TAB, this.sanitizer);
  }

  @HostListener('document:keydown', ['$event'])
  handleKeydown(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      this.close();
    }

    if (event.key === 'Tab') {
      const focusables = this.getFocusableElements();
      if (focusables.length === 0) return;

      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      const active = document.activeElement;

      if (event.shiftKey && active === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && active === last) {
        event.preventDefault();
        first.focus();
      }
    }
  }

  private getFocusableElements(): HTMLElement[] {
    const root = this.modalRootRef.nativeElement;
    return Array.from(
      root.querySelectorAll<HTMLElement>('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])')
    ).filter(el => !el.hasAttribute('disabled'));
  }

  onKeyDown(event: KeyboardEvent, button: string): void {
    if (button === 'close' && (event.key === 'Enter' || event.code === 'Space')) {
      this.close();
    }
    if (button === 'call' && (event.key === 'Enter' || event.code === 'Space')) {
      this.onBtnClick();
    }
  }
}
